import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faPlus,
  faArrowRight,
  faArrowLeft,
  faTicketAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import Datetime from "react-datetime";
import moment from "moment-timezone";
import { getAllChannel } from "../../../../controller/api/__classic__/channel";
import { includesSearchText } from "../../../../helper/text";
import { getClassicWeekdayKey } from "../../../../helper/helper";
import {
  addStorage,
  addPairStorage,
} from "../../../../controller/api/__classic__/input";
import { getList as getResultList } from "../../../../controller/api/__classic__/result";
// import { parseWeekday } from "../../../../helper/helper"; // Removing this line
import { AddTicketTableRow } from "../../ticket-storage/sub-screen/add-ticket-popup/component/AddTicketTableRow";
import { AddPairTicketTableRow } from "../../ticket-storage/sub-screen/add-ticket-popup/component/AddPairTicketTableRow";
import { AddTicketTableActionRow } from "../../ticket-storage/sub-screen/add-ticket-popup/component/AddTicketTableActionRow";

const STEP_SELECT_CHANNEL = 1;
const STEP_INPUT_TICKETS = 2;

const REGION_OPTIONS = [
  { value: "MN", label: "Miền Nam" },
  { value: "MT", label: "Miền Trung" },
];

const getRegionLabel = (value) => {
  return (
    REGION_OPTIONS.find((item) => item.value === value)?.label || "Miền Bắc"
  );
};

export default function AddTicketWithChannelPopup(props) {
  const { show, onHide, region, onRegionChange, onSuccess } = props;

  const [cookies] = useCookies(["lottmekong"]);

  const [step, setStep] = useState(STEP_SELECT_CHANNEL);
  const [popupRegion, setPopupRegion] = useState(region || "MN");
  const [allChannels, setAllChannels] = useState([]);
  const [listChannel, setListChannel] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelSearch, setChannelSearch] = useState("");
  const [quickDateFilter, setQuickDateFilter] = useState(null);
  const [resultDate, setResultDate] = useState(null);
  const [isCreatingPeriod, setIsCreatingPeriod] = useState(false);
  const [periodId, setPeriodId] = useState(null);

  const [singleData, setSingleData] = useState([]);
  const [pairData, setPairData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const maxDigits = popupRegion === "MB" ? 5 : 6;

  useEffect(() => {
    if (show) {
      setPopupRegion(region || "MN");
    }
  }, [show, region]);

  useEffect(() => {
    if (show) {
      const fetchChannels = async () => {
        const response = await getAllChannel(cookies.lottmekong, popupRegion);
        if (response && response.data && response.data.length > 0) {
          setAllChannels(response.data);
          setChannelSearch("");

          const selectedDate = resultDate || moment();
          setResultDate(selectedDate);
          if (!resultDate) {
            setQuickDateFilter("today");
          }

          const weekday = getClassicWeekdayKey(selectedDate);
          const filtered = response.data.filter(
            (ch) => ch.weekdays && ch.weekdays.includes(weekday),
          );
          setListChannel(filtered);
        } else {
          setAllChannels([]);
          setListChannel([]);
        }
      };
      fetchChannels();
    }
  }, [show, cookies.lottmekong, popupRegion, resultDate]);

  useEffect(() => {
    if (!show) {
      setStep(STEP_SELECT_CHANNEL);
      setSelectedChannel(null);
      setResultDate(null);
      setQuickDateFilter(null);
      setPeriodId(null);
      setChannelSearch("");
      setSingleData([]);
      setPairData([]);
      setSubmitError(null);
    }
  }, [show]);

  const isValidDateForPicker = useCallback((currentDate) => {
    // Allow today and future
    return currentDate.isSameOrAfter(moment(), "day");
  }, []);

  const handleChangeResultDate = useCallback(
    (payload) => {
      setResultDate(payload);
      setSelectedChannel(null);
      setQuickDateFilter(null);
      setChannelSearch("");

      // Filter channels by new date
      if (allChannels.length > 0) {
        const weekday = getClassicWeekdayKey(payload);
        const filtered = allChannels.filter(
          (ch) => ch.weekdays && ch.weekdays.includes(weekday),
        );
        setListChannel(filtered);
      }
    },
    [allChannels],
  );

  const onChannelSelect = (evt) => {
    if (evt.target.value) {
      const channel = JSON.parse(evt.target.value);
      setSelectedChannel(channel);
    } else {
      setSelectedChannel(null);
    }
  };

  const handleQuickDateSelect = useCallback(
    (type) => {
      const today = moment();
      let targetDate = null;

      if (type === "today") {
        targetDate = today.clone();
      } else if (type === "tomorrow") {
        targetDate = today.clone().add(1, "day");
      }

      setQuickDateFilter(type);
      if (targetDate) {
        setResultDate(targetDate);
        setSelectedChannel(null);
        setChannelSearch("");

        const weekday = getClassicWeekdayKey(targetDate);
        const filtered = allChannels.filter(
          (ch) => ch.weekdays && ch.weekdays.includes(weekday),
        );
        setListChannel(filtered);
      }
    },
    [allChannels],
  );

  const handleChangeRegion = useCallback(
    (evt) => {
      const nextRegion = evt.target.value;
      setPopupRegion(nextRegion);
      setSelectedChannel(null);
      setChannelSearch("");
      setAllChannels([]);
      setListChannel([]);
      setStep(STEP_SELECT_CHANNEL);
      setPeriodId(null);

      if (onRegionChange) {
        onRegionChange(nextRegion);
      }
    },
    [onRegionChange],
  );

  const visibleListChannel = useMemo(() => {
    const filtered = channelSearch
      ? listChannel.filter((ch) => includesSearchText(ch.name, channelSearch))
      : listChannel;

    if (
      selectedChannel &&
      !filtered.some((ch) => `${ch.id}` === `${selectedChannel.id}`)
    ) {
      const selectedFromList =
        listChannel.find((ch) => `${ch.id}` === `${selectedChannel.id}`) ||
        selectedChannel;
      return [selectedFromList, ...filtered].filter(Boolean);
    }

    return filtered;
  }, [channelSearch, listChannel, selectedChannel]);

  const handleNextStep = async () => {
    if (!selectedChannel) {
      alert("Vui lòng chọn đài");
      return;
    }
    if (!resultDate) {
      alert("Vui lòng chọn ngày quay số");
      return;
    }

    setIsCreatingPeriod(true);
    try {
      const targetDay = moment(resultDate).startOf("day");
      const targetDayEnd = moment(resultDate).endOf("day");
      const response = await getResultList(
        cookies.lottmekong,
        {
          from: targetDay.toDate(),
          to: targetDayEnd.toDate(),
          channelId: selectedChannel.id,
          region: popupRegion,
        },
        0,
      );

      const list = response?.data || [];
      const targetDayKey = moment(resultDate).format("YYYY-MM-DD");

      console.log("Looking for period:", {
        targetDayKey,
        channelId: selectedChannel.id,
        listCount: list.length,
        list: list.map((it) => ({
          id: it.id,
          channelId: it?.channelId || it?.ClassicLotteryChannel?.id,
          resultDate: it?.resultDate,
          dialDate: it?.dialDate,
        })),
      });

      const existing = list.find((it) => {
        const channelId =
          it?.channelId ||
          it?.ClassicLotteryChannel?.id ||
          it?.classicChannelId;
        // Use dialDate if resultDate is not available, or check both
        const dateToCheck = it?.dialDate || it?.resultDate;
        const dayKey = moment(dateToCheck).format("YYYY-MM-DD");
        return (
          `${channelId}` === `${selectedChannel.id}` && dayKey === targetDayKey
        );
      });

      if (!existing?.id) {
        console.log("Period not found. List received:", list);
        alert(
          "Kỳ quay số chưa được tạo. Vui lòng tạo kỳ quay ở trang Kỳ quay số (/classic/period) trước khi nhập vé.",
        );
        return;
      }

      setPeriodId(existing.id);
      setStep(STEP_INPUT_TICKETS);
    } catch (error) {
      alert("Lỗi: " + error.message);
    } finally {
      setIsCreatingPeriod(false);
    }
  };

  const handleBackStep = () => {
    setStep(STEP_SELECT_CHANNEL);
    setSingleData([]);
    setPairData([]);
  };

  const onAddSingle = () => {
    // Get the last amount from singleData or pairData
    let lastAmount = "";
    if (singleData.length > 0) {
      lastAmount = singleData[singleData.length - 1].amount || "";
    } else if (pairData.length > 0) {
      lastAmount = pairData[pairData.length - 1].amount || "";
    }

    setSingleData([
      ...singleData,
      {
        numbers: "",
        amount: lastAmount,
        images: null,
      },
    ]);
  };

  const onAddPair = () => {
    // Get the last amount from pairData or singleData
    let lastAmount = "";
    if (pairData.length > 0) {
      lastAmount = pairData[pairData.length - 1].amount || "";
    } else if (singleData.length > 0) {
      lastAmount = singleData[singleData.length - 1].amount || "";
    }

    setPairData([
      ...pairData,
      {
        last5Numbers: "",
        amount: lastAmount,
        images: null,
      },
    ]);
  };

  const onChangeSingleAmount = (index, amount) => {
    const newData = [...singleData];
    newData[index] = { ...newData[index], amount };
    setSingleData(newData);
  };

  const onChangeSingleNumbers = (index, numbers) => {
    const trimmedNumbers = numbers.trim();
    if (trimmedNumbers.length > maxDigits) {
      return;
    }
    const newData = [...singleData];
    newData[index] = { ...newData[index], numbers: trimmedNumbers };
    setSingleData(newData);
  };

  const onChangeSingleImage = (index, imageFile) => {
    const newData = [...singleData];
    newData[index] = { ...newData[index], images: imageFile };
    setSingleData(newData);
  };

  const onDeleteSingle = (index) => {
    setSingleData(singleData.filter((_, idx) => idx !== index));
  };

  const onChangePairAmount = (index, amount) => {
    const newData = [...pairData];
    newData[index] = { ...newData[index], amount };
    setPairData(newData);
  };

  const onChangePairLast5Numbers = (index, numbers) => {
    const trimmedNumbers = numbers.trim();
    if (trimmedNumbers.length > 5) {
      return;
    }
    const newData = [...pairData];
    newData[index] = { ...newData[index], last5Numbers: trimmedNumbers };
    setPairData(newData);
  };

  const onChangePairImage = (index, imageFile) => {
    const newData = [...pairData];
    newData[index] = { ...newData[index], images: imageFile };
    setPairData(newData);
  };

  const onDeletePair = (index) => {
    setPairData(pairData.filter((_, idx) => idx !== index));
  };

  const onSubmit = async () => {
    setSubmitError(null); // Clear previous errors

    const validSingleData = singleData.filter(
      (it) => it.numbers.length === maxDigits && it.amount !== "",
    );
    const validPairData = pairData.filter(
      (it) => it.last5Numbers.length === 5 && it.amount !== "",
    );

    if (validSingleData.length === 0 && validPairData.length === 0) {
      setSubmitError("Vui lòng nhập ít nhất 1 vé hợp lệ");
      return;
    }

    setIsSubmitting(true);
    try {
      let hasError = false;
      let errorMessages = [];

      if (validSingleData.length > 0) {
        const resp = await addStorage(
          cookies.lottmekong,
          periodId,
          validSingleData,
          false,
        );
        if (!resp || !resp.success) {
          hasError = true;
          // Prioritize message (Vietnamese) over error code
          const errorMsg = resp?.message || resp?.error || "Lỗi không xác định";
          errorMessages.push(errorMsg);
        }
      }

      if (validPairData.length > 0) {
        const resp = await addPairStorage(
          cookies.lottmekong,
          periodId,
          validPairData,
          false,
        );
        if (!resp || !resp.success) {
          hasError = true;
          // Prioritize message (Vietnamese) over error code
          const errorMsg = resp?.message || resp?.error || "Lỗi không xác định";
          errorMessages.push(errorMsg);
        }
      }

      if (hasError) {
        setSubmitError(errorMessages.join("\n"));
      } else {
        setSubmitError(null);
        setSingleData([]);
        setPairData([]);
        if (onSuccess) onSuccess();
        onHide();
      }
    } catch (error) {
      setSubmitError("Lỗi: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const _onHide = () => {
    if (isSubmitting || isCreatingPeriod) return;
    onHide();
  };

  const totalTickets = singleData.length + pairData.length;

  const channelInfo = useMemo(() => {
    if (!selectedChannel) return null;
    return (
      <div className="d-flex align-items-center gap-2">
        {selectedChannel.logo && (
          <img
            src={selectedChannel.logo}
            alt={selectedChannel.name}
            className="rounded-circle border"
            style={{ width: "40px", height: "40px", objectFit: "cover" }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}
        <div>
          <strong>{selectedChannel.name}</strong>
          {resultDate && (
            <div className="text-muted small">
              {moment(resultDate).format("DD/MM/YYYY")}
            </div>
          )}
        </div>
      </div>
    );
  }, [selectedChannel, resultDate]);

  return (
    <Modal
      show={show}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      onHide={_onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <FontAwesomeIcon icon={faTicketAlt} className="me-2" />
          {step === STEP_SELECT_CHANNEL
            ? `Nhập vé - Chọn đài (${getRegionLabel(popupRegion)})`
            : `Nhập vé - ${selectedChannel?.name || ""}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {step === STEP_SELECT_CHANNEL && (
          <>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Khu vực <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={popupRegion}
                    onChange={handleChangeRegion}
                    size="lg"
                  >
                    {REGION_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Ngày quay số <span className="text-danger">*</span>
                  </Form.Label>
                  <Datetime
                    timeFormat={false}
                    isValidDate={isValidDateForPicker}
                    onChange={handleChangeResultDate}
                    value={resultDate || ""}
                    renderInput={(props, openCalendar) => (
                      <InputGroup>
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faCalendarAlt} />
                        </InputGroup.Text>
                        <Form.Control
                          required
                          type="text"
                          value={
                            resultDate
                              ? moment(resultDate).format("DD/MM/YYYY")
                              : ""
                          }
                          placeholder="Chọn ngày quay số"
                          onFocus={openCalendar}
                          onChange={() => {}}
                          size="lg"
                        />
                      </InputGroup>
                    )}
                  />
                  <div className="mt-2">
                    <ButtonGroup>
                      <Button
                        variant={
                          quickDateFilter === "today"
                            ? "primary"
                            : "outline-primary"
                        }
                        size="sm"
                        onClick={() => handleQuickDateSelect("today")}
                      >
                        Hôm nay
                      </Button>
                      <Button
                        variant={
                          quickDateFilter === "tomorrow"
                            ? "primary"
                            : "outline-primary"
                        }
                        size="sm"
                        onClick={() => handleQuickDateSelect("tomorrow")}
                      >
                        Ngày mai
                      </Button>
                    </ButtonGroup>
                  </div>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold">
                    Chọn đài ({listChannel.length}){" "}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup className="mb-2">
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Tìm đài..."
                      value={channelSearch}
                      onChange={(e) => setChannelSearch(e.target.value)}
                      size="lg"
                      disabled={!resultDate}
                    />
                  </InputGroup>
                  <Form.Select
                    value={
                      selectedChannel ? JSON.stringify(selectedChannel) : ""
                    }
                    onChange={onChannelSelect}
                    size="lg"
                    disabled={!resultDate}
                  >
                    <option value="">-- Chọn đài --</option>
                    {visibleListChannel.map((item) => (
                      <option key={item.id} value={JSON.stringify(item)}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                  {resultDate &&
                    !selectedChannel &&
                    listChannel.length === 0 && (
                      <div className="text-danger mt-1 small">
                        Không có đài nào mở thưởng vào ngày này.
                      </div>
                    )}
                </Form.Group>
              </Col>
            </Row>

            {selectedChannel && resultDate && (
              <div className="alert alert-info">
                <strong>Đài:</strong> {selectedChannel.name} |{" "}
                <strong>Ngày quay:</strong>{" "}
                {moment(resultDate).format("DD/MM/YYYY")}
              </div>
            )}
          </>
        )}

        {step === STEP_INPUT_TICKETS && (
          <>
            <div className="mb-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
              {channelInfo}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackStep}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
                Đổi đài
              </Button>
            </div>

            <Table
              responsive
              className="align-items-center table-bordered table-hover"
            >
              <thead className="text-center">
                <tr>
                  <th scope="col">STT</th>
                  <th scope="col">Dãy số</th>
                  <th scope="col">Số lượng</th>
                  <th scope="col">Hình</th>
                  <th scope="col">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {singleData.map((it, idx) => (
                  <AddTicketTableRow
                    onDelete={onDeleteSingle}
                    onChangeNumbers={onChangeSingleNumbers}
                    onChangeAmount={onChangeSingleAmount}
                    onChangeImage={onChangeSingleImage}
                    rIndex={idx}
                    key={`single-ticket-${idx}`}
                    maxDigits={maxDigits}
                    {...it}
                  />
                ))}
                {pairData.map((it, idx) => (
                  <AddPairTicketTableRow
                    onDelete={onDeletePair}
                    onChangeLast5Numbers={onChangePairLast5Numbers}
                    onChangeAmount={onChangePairAmount}
                    onChangeImage={onChangePairImage}
                    rIndex={idx}
                    key={`pair-ticket-${idx}`}
                    {...it}
                  />
                ))}
                {singleData.length === 0 && pairData.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      Chưa có vé nào. Nhấn nút bên dưới để thêm vé.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <AddTicketTableActionRow
                  region={popupRegion}
                  onAddSingle={onAddSingle}
                  onAddPair={onAddPair}
                />
              </tfoot>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-column align-items-stretch">
        {/* Error Message Display */}
        {submitError && (
          <div
            className="alert alert-danger mb-3 w-100"
            style={{
              whiteSpace: "pre-wrap",
              fontSize: "0.9rem",
              padding: "12px 16px",
              borderRadius: "8px",
            }}
          >
            <strong>❌ Lỗi:</strong>
            <div className="mt-1">{submitError}</div>
          </div>
        )}
        <div className="d-flex justify-content-end gap-2">
          <Button
            variant="secondary"
            onClick={_onHide}
            disabled={isSubmitting || isCreatingPeriod}
          >
            Đóng
          </Button>
          {step === STEP_SELECT_CHANNEL && (
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={!selectedChannel || !resultDate || isCreatingPeriod}
            >
              {isCreatingPeriod ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Đang xử lý...
                </>
              ) : (
                <>
                  Tiếp tục
                  <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </>
              )}
            </Button>
          )}
          {step === STEP_INPUT_TICKETS && (
            <Button
              variant="success"
              onClick={onSubmit}
              disabled={isSubmitting || totalTickets === 0}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Đang thêm...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Thêm vé ({totalTickets})
                </>
              )}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
